import { useDispatch, useSelector } from 'react-redux';
import {
  fetchNoticationList,
  markAllNotificatioRead,
  markNotificatioRead,
  resetList,
  selectNotificationData,
  selectNotificationLoading,
} from '../redux/notification.slice';
import { Box, IconButton, Menu } from '@mui/material';
import Images from '../Utils/images';
import { AIT } from '../Utils/Localization';
import { useEffect, useRef, useState } from 'react';
import CustomButton from './CustomButton';
import dayjs from 'dayjs';
import { NOTIFICATION_TYPES } from '../Utils/constantData';
import { useNavigate } from 'react-router-dom';
import LoadingNotification from './LoadingNotification';
import NoNotification from './NoNotification';
import { useAppSelector } from '../redux/store';
import { ROUTES } from '../helpers/contants';
import { notify } from '../Utils/toastify';
import { checkAccess } from '../redux/ProductionSlice';
const relativeTime = require('dayjs/plugin/relativeTime');
dayjs.extend(relativeTime);

/**
 * Manages the display, fetching, and interaction with in-app notifications.
 * @example
 * manageNotifications()
 * Displays the notification panel and updates notifications status.
 * @param {React.MouseEvent<HTMLButtonElement>} event - The event object for click interactions.
 * @returns {JSX.Element} Rendered notifications component.
 * @description
 *   - Utilizes Redux hooks to interact with the global state and dispatch actions.
 *   - Listens to scroll events to fetch more notifications when the end of the list is reached.
 *   - Contains logic for handling notification read/unread status and triggering navigation based on notification type.
 */
const InAppNotification = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector(selectNotificationLoading);
  const data = useSelector(selectNotificationData);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [entityId, setEntityId] = useState<string>('');
  // const { t } = useTranslation('translation', {
  //   keyPrefix: 'notification',
  // })
  // const { t: tC } = useTranslation('translation', {
  //   keyPrefix: 'Common',
  // })
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const openPopUp = Boolean(anchorEl);
  const boxRef = useRef<HTMLUListElement>(null); // Ul Ref
  const prevScrollPosition = useRef<number>(0);
  const handleClickNotice = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseNotice = () => {
    setAnchorEl(null);
  };
  const { access } = useAppSelector((state: any) => state.production);

  const handleAccess = () => {
    if (access.type === 'ACCESS_SUCCESSFUL') {
      navigate(`${ROUTES.PRODUCTION_DETAIL}/${entityId}`);
    } else {
      notify(access.message, 'error');
    }
  };

  useEffect(() => {
    if (access && entityId) {
      handleAccess();
    }
  }, [access]);

  // const socketClient = useSocket()

  /**
   * Checks if the user has scrolled to the bottom of the element and updates the page number if applicable.
   * @example
   * onScrollHandler()
   * // Page number is updated if conditions are met.
   * @param {Object} boxRef - Ref object of the scrollable container.
   * @returns {void} Does not return any value.
   * @description
   *   - Updates the page number if scrolled to the bottom and loading status is not 'pending'.
   *   - Sets prevScrollPosition to the current scrollTop value.
   */
  const handleScroll = () => {
    if (boxRef.current) {
      const { scrollTop, clientHeight, scrollHeight } = boxRef.current;

      if (
        Math.ceil(scrollTop + clientHeight) >= scrollHeight &&
        loading !== 'pending' &&
        hasMore
      ) {
        data?.data && data?.data.pageNo && setPageNumber(data?.data.pageNo + 1);
      }
      prevScrollPosition.current = scrollTop;
    }
  };

  useEffect(() => {
    if (boxRef.current) {
      boxRef.current.scrollTop = prevScrollPosition.current;
    }
    if (data && data?.data && data?.data.totalPage) {
      setHasMore(data?.data.totalPage >= pageNumber);
    }
  }, [data?.data.pageNo]);

  useEffect(() => {
    if (loading == 'idle' && hasMore) {
      dispatch(fetchNoticationList({ page: pageNumber, pageSize: 5 }));
    }
  }, [loading]);

  useEffect(() => {
    if (hasMore && loading !== 'idle' && loading !== 'pending') {
      dispatch(fetchNoticationList({ page: pageNumber, pageSize: 5 }));
    }
  }, [dispatch, pageNumber, hasMore]);

  useEffect(() => {
    setTimeout(() => {
      if (boxRef.current) {
        boxRef.current.addEventListener('scroll', handleScroll); // Attach event listener to the referenced element
        return () => {
          if (boxRef.current) {
            boxRef.current.removeEventListener('scroll', handleScroll); // Remove event listener when component unmounts
          }
        };
      }
    }, 200);
  }, [handleScroll, boxRef.current, openPopUp]);

  /**
   * Handles the process of marking a notification as read, fetching the updated notification list, and handling redirection.
   * @example
   * handleNotificationClick(notification)
   * Redirects user after marking notification as read.
   * @param {Object} n - The notification object.
   * @returns {void} Doesn't return anything.
   * @description
   *   - Closes the notice popup first.
   *   - Resets page number and notification list if the notification is not read.
   *   - Marks the notification as read and fetches an updated list.
   *   - Handles user redirection based on the notification.
   */
  // const handleNotificationClick = (n: any) => {
  //   handleCloseNotice();
  //   if (!n.isRead) {
  //     setPageNumber(1);
  //     dispatch(resetList());
  //     markNotificatioRead(n._id, () => {
  //       dispatch(fetchNoticationList({ page: pageNumber, pageSize: 5 }));
  //       handleRedirection(n);
  //     });
  //   } else {
  //     handleRedirection(n);
  //   }
  // };

  /**
   * Navigates to specific pages based on the notification type provided in the argument.
   * @example
   * handleNotification({
   *   inAppNotificationData: {
   *     notificationType: 'Vendor',
   *     vendorId: '12345'
   *   }
   * })
   * // Navigates to: '/vendor-profile/12345'
   * @param {Object} n - The notification object containing notification data.
   * @returns {void} No return value. The function triggers navigation based on notification type.
   * @description
   *   - The function distinguishes between different notification types including Vendor, CHAT, PRODUCTION, and STUDIO.
   *   - Uses the `navigate()` function to redirect users to relevant pages.
   *   - The function is designed to handle future expandability with various subNotificationTypes as evident from commented-out code.
   */
  // const handleRedirection = (n: any) => {
  //   if (
  //     n.inAppNotificationData.notificationType === NOTIFICATION_TYPES.Vendor
  //   ) {
  //     navigate(`/vendor-profile/${n?.inAppNotificationData?.vendorId}`);
  //   } else if (
  //     n.inAppNotificationData.notificationType === NOTIFICATION_TYPES.CHAT
  //   ) {
  //     // socketClient &&
  //     //   socketClient.activeChat({ chatId: n.inAppNotificationData.chatId })
  //     navigate('/chats');
  //   } else if (
  //     n.inAppNotificationData.notificationType === NOTIFICATION_TYPES.PRODUCTION
  //   ) {
  //     navigate(
  //       `/view-request/${n?.inAppNotificationData?.addedByUserId}?requestId=${n?.inAppNotificationData?.requestId}`
  //     );
  //   } else if (
  //     n.inAppNotificationData.notificationType === NOTIFICATION_TYPES.STUDIO
  //   ) {
  //     navigate(
  //       `/view-request/${n?.inAppNotificationData?.addedByUserId}?requestId=${n?.inAppNotificationData?.requestId}`
  //     );
  //   }
  // };

  const handleNotificationClick = (n: any) => {
    handleCloseNotice();
    if (!n.isRead) {
      setPageNumber(1);
      dispatch(resetList());
      markNotificatioRead(n._id, () => {
        dispatch(fetchNoticationList({ page: pageNumber, pageSize: 5 }));
        handleRedirection(n);
      });
    } else {
      handleRedirection(n);
    }
  };

  const handleRedirection = (n: any) => {
    if (n.inAppNotificationData.notificationType === NOTIFICATION_TYPES.CHAT) {
      socketClient &&
        socketClient.activeChat({ chatId: n.inAppNotificationData.chatId });
      navigate(ROUTES.CHATS);
    } else if (
      n.inAppNotificationData.notificationType === NOTIFICATION_TYPES.ORDER
    ) {
      if (
        n.inAppNotificationData.subNotificationType ===
        NOTIFICATION_TYPES.ORDER_REQUEST
      ) {
        navigate(
          `${ROUTES.ORDERS_DETAILS.replace(
            ':id',
            n.inAppNotificationData?.orderId,
          )}?type=request&tabType=ORDER_REQUEST`,
          { state: { fromNotification: true } },
        );
      } else if (
        n.inAppNotificationData.subNotificationType === 'VENDOR_ORDER_REQUEST'
      ) {
        navigate(
          `${ROUTES.ORDERS_DETAILS.replace(
            ':id',
            n.inAppNotificationData?.orderId,
          )}?type=vendor&tabType=VENDOR_REQUEST`,
          { state: { fromNotification: true } },
        );
      } else if (n?.inAppNotificationData?.subNotificationType === 'RETURN') {
        navigate(
          `${ROUTES.RETURN_ORDER_DETAIL}/${n.inAppNotificationData?.vendorOrderId}?type=return&tabType=RETURN_REQUEST`,
          { state: { fromNotification: true } },
        );
      } else if (
        n.inAppNotificationData.orderId ||
        n.inAppNotificationData.vendorOrderId
      ) {
        navigate(
          `${ROUTES.ORDERS_DETAILS.replace(
            ':id',
            n.inAppNotificationData?.orderId ||
              n.inAppNotificationData?.vendorOrderId,
          )}?type=list&tabType=MY_ORDERS`,
          { state: { fromNotification: true } },
        );
      } else {
        window.location.href = `${ROUTES.ORDERS_LIST}`;
      }
      return;
    } else if (n?.inAppNotificationData?.notificationType === 'PAYMENT') {
      navigate(ROUTES.PAYMENT_HISTORY);
    } else if (n?.inAppNotificationData?.notificationType === 'PROFILE') {
      navigate(ROUTES.PROFILE);
    } else if (
      n.inAppNotificationData.subNotificationType ===
      NOTIFICATION_TYPES.VENDOR_CHANGEPASS
    ) {
      navigate(ROUTES.PROFILE);
    } else if (
      n.inAppNotificationData.subNotificationType ===
      NOTIFICATION_TYPES.VENDOR_DOC_EXPIRED
    ) {
      navigate(ROUTES.COMPANY);
    } else if (
      n?.inAppNotificationData?.notificationType ===
      NOTIFICATION_TYPES.PRODUCTION
    ) {
      if (n?.inAppNotificationData?.title === 'Leave Access') return;
      setEntityId(n?.inAppNotificationData?.entityId);
      checkAccess(
        n?.inAppNotificationData?.entityType ||
          n?.inAppNotificationData?.entityModelType,
        n?.inAppNotificationData?.entityId,
      );
    } else if (
      n?.inAppNotificationData?.notificationType ===
        NOTIFICATION_TYPES.INVOICE &&
      n?.inAppNotificationData?.subNotificationType ===
        'INVOICE_APPROVAL_PENDING'
    ) {
      navigate(`${ROUTES.INVOICE_REQUESTS}`, {
        state: { fromNotification: true },
      });
    } else if (
      n?.inAppNotificationData?.notificationType === NOTIFICATION_TYPES.INVOICE
    ) {
      navigate(
        `${ROUTES.INVOICE_DETAIL}/${n?.inAppNotificationData?.entityId}`,
        { state: { fromNotification: true } },
      );
    } else if (
      n?.inAppNotificationData?.notificationType === NOTIFICATION_TYPES.RISK
    ) {
      if (
        n?.inAppNotificationData?.subNotificationType ===
        'NEW_THREAT_REPORT_CREATED'
      ) {
        // navigate(
        //   `${ROUTES.LOCATION_SECURITY_DASHBOARD}/?locationId=${n?.inAppNotificationData?.locationId}&name=${n?.inAppNotificationData?.locationName}`,
        //   { state: { fromNotification: true } },
        // );
        navigate(
          `${ROUTES.ACTIVE_THREATS}?entityType=${'locations'}&entityId=${
            n?.inAppNotificationData?.locationId
          }&threatLevel=${'ALL'}`,
          { state: { fromNotification: true } },
        );
      } else if (
        n?.inAppNotificationData?.subNotificationType ===
        'CRITICAL_THREAT_IDENTIFIED'
      ) {
        navigate(
          `${ROUTES.THREAT_DETAIL}/${n?.inAppNotificationData?.entityId}`,
          { state: { fromNotification: true } },
        );
      } else if (
        n?.inAppNotificationData?.subNotificationType === 'THREAT_UPDATED'
      ) {
        navigate(
          `${ROUTES.ACTIVE_THREATS}?entityType=${'locations'}&entityId=${
            n?.inAppNotificationData?.locationId
          }&threatLevel=${'ALL'}`,
          { state: { fromNotification: true } },
        );
        // navigate(
        //   `${ROUTES.LOCATION_SECURITY_DASHBOARD}/?locationId=${n?.inAppNotificationData?.locationId}&name=${n?.inAppNotificationData?.locationName}`,
        //   { state: { fromNotification: true } },
        // );
      } else if (
        n?.inAppNotificationData?.subNotificationType === 'THREAT_ACKNOWLEDGED'
      ) {
        navigate(
          `${ROUTES.ACTIVE_THREATS}?entityType=${'locations'}&entityId=${
            n?.inAppNotificationData?.locationId
          }&threatLevel=${'ALL'}`,
          { state: { fromNotification: true } },
        );
        // navigate(
        //   `${ROUTES.LOCATION_SECURITY_DASHBOARD}/?locationId=${n?.inAppNotificationData?.locationId}&name=${n?.inAppNotificationData?.locationName}`,
        //   { state: { fromNotification: true } },
        // );
      } else if (
        n?.inAppNotificationData?.subNotificationType === 'THREAT_REJECTED'
      ) {
        navigate(
          `${ROUTES.ACTIVE_THREATS}?entityType=${'locations'}&entityId=${
            n?.inAppNotificationData?.locationId
          }&threatLevel=${'ALL'}`,
          { state: { fromNotification: true } },
        );
        // navigate(
        //   `${ROUTES.LOCATION_SECURITY_DASHBOARD}/?locationId=${n?.inAppNotificationData?.locationId}&name=${n?.inAppNotificationData?.locationName}`,
        //   { state: { fromNotification: true } },
        // );
      }
    } else if (n?.inAppNotificationData?.notificationType === 'VTA') {
      navigate(
        `${ROUTES.LOCATION_SECURITY_DASHBOARD}/?locationId=${n?.inAppNotificationData?.locationId}&name=${n?.inAppNotificationData?.locationName}`,
        { state: { fromNotification: true } },
      );
    } else if (
      n?.inAppNotificationData?.notificationType === 'CRITICAL_THREAT'
    ) {
      navigate(
        `${ROUTES.LOCATION_SECURITY_DASHBOARD}/?locationId=${n?.inAppNotificationData?.locationId}&name=${n?.inAppNotificationData?.locationName}`,
        { state: { fromNotification: true } },
      );
    } else if (
      ['SET', 'LOCATION', 'PRODUCTION', 'EPISODE', 'STUDIO'].includes(
        n?.inAppNotificationData?.notificationType,
      )
    ) {
      // console.log('====>>>>', n?.inAppNotificationData?.notificationType);
      navigate(
        `/${
          n?.inAppNotificationData?.notificationType?.toLowerCase() + '-detail'
        }/${n?.inAppNotificationData?.entityId}`,
      );
    }
    // else if (n.inAppNotificationData.notificationType === BOOKING_ALERT) {
    //   navigate(ROUTES.BOOK_CAB);
    // }
  };
  const unreadCountShow =
    (data && data?.data.unreadCount && data?.data.unreadCount > 0) || null;

  return (
    <div className="notification_action">
      <IconButton
        onClick={handleClickNotice}
        className="NotificationBell notification-btn"
      >
        <img src={Images.BELL} alt={AIT().notifications} />
        <span className=" count">
          {unreadCountShow && (
            <span className="NotificationCount ">
              {data?.data?.unreadCount}
            </span>
          )}
        </span>
      </IconButton>
      {openPopUp && (
        <Menu
          id="notification"
          anchorEl={anchorEl}
          open={openPopUp}
          onClose={handleCloseNotice}
          MenuListProps={{
            'aria-labelledby': 'notification',
          }}
          className="notification_dropdown"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: -25,
            horizontal: 'right',
          }}
        >
          <Box className="notifications">
            <div className="noticeHeader">
              <h3>Notifications ({(data && data.data.total) || 0})</h3>
              {data?.data && data?.data.unreadCount !== 0 ? (
                <CustomButton
                  size="large"
                  variant="text"
                  text={`Read All`}
                  showIcon={false}
                  width="auto"
                  type="button"
                  id="read_all"
                  className="read_all_btn"
                  onClick={() => {
                    handleCloseNotice();
                    markAllNotificatioRead(() => {
                      dispatch(fetchNoticationList({ page: 1, pageSize: 5 }));
                    });
                    setPageNumber(1);
                    dispatch(resetList());
                  }}
                />
              ) : (
                <></>
              )}

              <IconButton
                onClick={handleCloseNotice}
                className="close_notification"
              >
                <img src={Images.CLOSE} alt={AIT().close} />
              </IconButton>
            </div>
            {data &&
            data.data &&
            data?.data.data &&
            data?.data.data.length > 0 ? (
              <ul className="scroll-list" ref={boxRef}>
                {data.data.data.map((d: any) => (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
                  <li
                    key={d._id}
                    onClick={() => {
                      handleNotificationClick(d);
                    }}
                  >
                    <figure>
                      <img src={Images.BELL_WHITE} alt={AIT().notification} />
                    </figure>
                    <div className="notice_text">
                      <div className="title">
                        <h3>{d.inAppNotificationData.title}</h3>
                        <span>{dayjs(d.createdAt).fromNow(true)} ago</span>
                      </div>
                      <p>
                        {['PRODUCTION', 'STUDIO']?.includes(
                          d?.inAppNotificationData.notificationType,
                        )
                          ? d?.message
                          : d.inAppNotificationData?.body}
                      </p>
                    </div>
                    <div className="unread">
                      {!d.isRead && (
                        <img
                          src={Images.UNREAD_NOTIFICATION}
                          alt="New message"
                        />
                      )}
                    </div>
                  </li>
                ))}
                {loading === 'pending' && (
                  <LoadingNotification isLoading={true} />
                )}
              </ul>
            ) : (
              <></>
            )}
            {loading === 'fulfilled' &&
              data &&
              data.data &&
              data?.data.data &&
              data?.data.data.length === 0 && <NoNotification />}
            {/* <NoNotification /> */}
          </Box>
        </Menu>
      )}
    </div>
  );
};

export default InAppNotification;
